import React, { useEffect, useRef, useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { FundingBankChipContent } from '@wavvest/wealth-ui';
import { FundingChip } from '@/util/bank-link';
import { useFundingBankReplace } from './FundingBankReplaceContext';
import FundingBankReplacePanel from './FundingBankReplacePanel';

type FundingBankChipProps = {
  chip: FundingChip;
};

const FundingBankChip: React.FC<FundingBankChipProps> = ({ chip }) => {
  const { walletFor, pendingSources, onUpdated, openRequest } = useFundingBankReplace();
  const [open, setOpen] = useState(false);
  const tooltip = chip.status === 'not_set' ? 'Add a funding bank' : 'Change funding bank';
  const wallet = walletFor(chip.accountId);

  // Ignore a request made before this chip mounted, so a remount doesn't reopen it.
  const handledNonceRef = useRef(openRequest?.nonce ?? 0);
  useEffect(() => {
    if (!openRequest || openRequest.nonce === handledNonceRef.current) {
      return;
    }
    handledNonceRef.current = openRequest.nonce;
    if (openRequest.accountId === chip.accountId) {
      setOpen(true);
    }
  }, [openRequest, chip.accountId]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.funding-bank-replace-popover')) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      arrow={false}
      zIndex={1200}
      overlayClassName="funding-bank-replace-popover"
      destroyOnHidden
      getPopupContainer={() => document.body}
      content={
        <FundingBankReplacePanel
          chip={chip}
          wallet={wallet}
          pendingSources={pendingSources}
          onClose={() => setOpen(false)}
          onUpdated={onUpdated}
        />
      }
    >
      <Tooltip title={open ? '' : tooltip} trigger={['hover', 'focus']}>
        <button
          type="button"
          className={`funding-bank-chip is-${chip.status}`}
          aria-label={tooltip}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <FundingBankChipContent chip={chip} />
        </button>
      </Tooltip>
    </Popover>
  );
};

export default FundingBankChip;
